/**
 * The workspace: the pane beside a conversation where artifacts open (a draft to edit, an invoice, a
 * thread, a contract review). Each AgentSurface owns one and provides it to everything it renders, so an
 * artifact chip anywhere in the transcript opens in that conversation's pane.
 *
 * Several records can be open at once (tabs, newest first); opening one that's already open focuses it.
 *
 * Phones can minimize the workspace: the sheet goes away but the tabs stay open behind a capsule
 * (WorkspaceCapsule) until restored. `presented` is what a sheet should bind its `open` to.
 *
 * On phones the pane is a sheet (`sheet`, set by the pane), and a record opened from inside another (a thread
 * from a draft, a draft from a "3 drafts" list) is pushed over it in the same tab: `current` is the top of the
 * active tab's stack and `back()` returns to the record under it, like a navigation stack. Elsewhere a record
 * opened from inside another gets a tab of its own, as before.
 */
import { computed, inject, reactive, type InjectionKey } from 'vue'
import { artifactKey, artifactPrefersWide, type ArtifactRef } from './artifacts'

const MAX_TABS = 6
/** Records stacked over one tab (phones). Deeper pushes drop the oldest pushed record. */
const MAX_DEPTH = 8

export interface AgentWorkspace {
    readonly tabs: ArtifactRef[]
    readonly activeKey: string | null
    /** The active tab (the record it was opened as). */
    readonly active: ArtifactRef | null
    /** The record on screen: the top of the active tab's stack, else the tab itself. */
    readonly current: ArtifactRef | null
    readonly isOpen: boolean
    /** Open, but tucked away behind a capsule (phones). Cleared by `restore()`, `open()` and closing the last tab. */
    readonly minimized: boolean
    /** On screen: open and not minimized. */
    readonly presented: boolean
    minimize(): void
    restore(): void
    /** The pane is a phone sheet: records opened from inside a record stack over it, and full-screen things
     *  opened from it (overlays, the previewer) minimize it first. The pane sets this. */
    readonly sheet: boolean
    setSheet(on: boolean): void
    /** The records pushed over a tab (oldest first; empty when the tab itself is on screen). */
    stackOf(key: string): readonly ArtifactRef[]
    /** The active tab has a record pushed over it: `back()` returns to the one under it. */
    readonly canGoBack: boolean
    /**
     * Open a record from inside the one on screen (a row in a group, a thread linked from a draft). In a sheet it
     * is pushed over the current record; elsewhere it opens as its own tab. Unlike `open`, a single email draft is
     * allowed here: the creator asked for that draft from a list, and it has no place in the conversation.
     */
    push(a: ArtifactRef): void
    /** Pop the active tab's stack. False when there was nothing to pop. */
    back(): boolean
    /** The open item wants the wide pane: its type's default until the creator expands or shrinks it. */
    readonly activeWide: boolean
    toggleWide(): void
    /** An email draft is being shown in the conversation (an inline draft registers itself while mounted). */
    trackChatDraft(id: string): () => void
    /** The conversation has an email draft that hasn't been sent or discarded yet. */
    readonly hasActiveChatDraft: boolean
    /** A draft shown in this conversation has been sent. */
    readonly hasSentChatDraft: boolean
    open(a: ArtifactRef): void
    activate(key: string): void
    /** Close a tab (and whatever was pushed over it); the active one by default. */
    close(key?: string): void
    closeAll(): void
}

export const WORKSPACE_KEY: InjectionKey<AgentWorkspace> = Symbol('agentWorkspace')

export function createWorkspace(): AgentWorkspace {
    const st = reactive({
        tabs: [] as ArtifactRef[],
        activeKey: null as string | null,
        wide: {} as Record<string, boolean>,
        chatDrafts: [] as string[],
        minimized: false,
        sheet: false,
        stacks: {} as Record<string, ArtifactRef[]>,
    })
    const active = computed(() => st.tabs.find((t) => artifactKey(t) === st.activeKey) ?? null)
    const current = computed(() => {
        const a = active.value
        if (!a) return null
        return st.stacks[artifactKey(a)]?.at(-1) ?? a
    })
    const activeWide = computed(() => {
        const a = current.value
        if (!a) return false
        return st.wide[artifactKey(a)] ?? artifactPrefersWide(a)
    })
    const hasActiveChatDraft = computed(() => st.chatDrafts.some((id) => !isDraftSent(id) && !isDraftDiscarded(id)))
    const hasSentChatDraft = computed(() => st.chatDrafts.some((id) => isDraftSent(id)))

    function openTab(a: ArtifactRef) {
        const key = artifactKey(a)
        const at = st.tabs.findIndex((t) => artifactKey(t) === key)
        if (at >= 0) {
            st.tabs[at] = { ...st.tabs[at], ...a }
            // Opening a tab's own record again shows it, not whatever was pushed over it.
            delete st.stacks[key]
        } else {
            const next = [a, ...st.tabs]
            for (const dropped of next.slice(MAX_TABS)) delete st.stacks[artifactKey(dropped)]
            st.tabs = next.slice(0, MAX_TABS)
        }
        st.activeKey = key
        st.minimized = false
    }

    return {
        get hasActiveChatDraft() { return hasActiveChatDraft.value },
        get hasSentChatDraft() { return hasSentChatDraft.value },
        trackChatDraft(id) {
            st.chatDrafts.push(id)
            return () => {
                const at = st.chatDrafts.indexOf(id)
                if (at >= 0) st.chatDrafts.splice(at, 1)
            }
        },
        get activeWide() { return activeWide.value },
        toggleWide() {
            const a = current.value
            if (a) st.wide[artifactKey(a)] = !activeWide.value
        },
        get tabs() { return st.tabs },
        get activeKey() { return st.activeKey },
        get active() { return active.value },
        get current() { return current.value },
        get isOpen() { return active.value !== null },
        get minimized() { return st.minimized && active.value !== null },
        get presented() { return active.value !== null && !st.minimized },
        minimize() {
            if (active.value) st.minimized = true
        },
        restore() {
            st.minimized = false
        },
        get sheet() { return st.sheet },
        setSheet(on) {
            st.sheet = on
        },
        stackOf(key) {
            return st.stacks[key] ?? []
        },
        get canGoBack() {
            return Boolean(st.activeKey && st.stacks[st.activeKey]?.length)
        },
        push(a) {
            const tab = active.value
            if (!st.sheet || !tab) {
                openTab(a)
                return
            }
            const key = artifactKey(a)
            const tabKey = artifactKey(tab)
            const stack = st.stacks[tabKey] ?? []
            st.minimized = false
            // Already in this tab's path: go back to it rather than stacking a copy.
            if (key === tabKey) {
                delete st.stacks[tabKey]
                return
            }
            const at = stack.findIndex((x) => artifactKey(x) === key)
            if (at >= 0) {
                st.stacks[tabKey] = stack.slice(0, at + 1)
                return
            }
            st.stacks[tabKey] = [...stack, a].slice(-MAX_DEPTH)
        },
        back() {
            const k = st.activeKey
            const stack = k ? st.stacks[k] : undefined
            if (!k || !stack?.length) return false
            if (stack.length === 1) delete st.stacks[k]
            else st.stacks[k] = stack.slice(0, -1)
            return true
        },
        open(a) {
            // A single email draft lives in the conversation, never in the pane (a draft row in a group list
            // opens through `push`).
            if (a.type === 'draft' && !a.items) return
            openTab(a)
        },
        activate(key) {
            if (st.tabs.some((t) => artifactKey(t) === key)) st.activeKey = key
        },
        close(key) {
            const k = key ?? st.activeKey
            const at = st.tabs.findIndex((t) => artifactKey(t) === k)
            if (at < 0 || k === null) return
            st.tabs = st.tabs.filter((_, i) => i !== at)
            delete st.stacks[k]
            if (st.activeKey === k) {
                const next = st.tabs[Math.min(at, st.tabs.length - 1)]
                st.activeKey = next ? artifactKey(next) : null
            }
            if (!st.tabs.length) st.minimized = false
        },
        closeAll() {
            st.tabs = []
            st.activeKey = null
            st.minimized = false
            st.stacks = {}
        },
    }
}

/**
 * Drafts the creator has sent from a workspace pane (or opened already sent), so their chips stop offering
 * "Review & send". App-wide: draft ids are unique, and a send in one pane should show everywhere.
 */
const sentDrafts = reactive(new Set<string>())
export function markDraftSent(id: string): void {
    sentDrafts.add(id)
}
export function isDraftSent(id: string): boolean {
    return sentDrafts.has(id)
}
/** A send that was taken for gone out turned out not to (the provider rejected it later): offer the draft again. */
export function unmarkDraftSent(id: string): void {
    sentDrafts.delete(id)
}

/**
 * Contract reviews the creator has opened, so their chips stop glowing for attention. Kept on this device
 * (the last 200) so a reload doesn't light up reviews they've already read.
 */
const SEEN_REVIEWS_KEY = 'lh:agent-seen-reviews'
const seenReviews = reactive(new Set<string>(readSeenReviews()))
function readSeenReviews(): string[] {
    try {
        const list = JSON.parse(localStorage.getItem(SEEN_REVIEWS_KEY) || '[]')
        return Array.isArray(list) ? list.map(String) : []
    } catch {
        return []
    }
}
export function markReviewSeen(id: string): void {
    if (seenReviews.has(id)) return
    seenReviews.add(id)
    try {
        localStorage.setItem(SEEN_REVIEWS_KEY, JSON.stringify([...seenReviews].slice(-200)))
    } catch {
        /* private mode / storage blocked: it just stops glowing for this session */
    }
}
export function isReviewSeen(id: string): boolean {
    return seenReviews.has(id)
}

/** Drafts the creator deleted from the conversation or a pane: they show as discarded wherever they appear. */
const discardedDrafts = reactive(new Set<string>())
export function markDraftDiscarded(id: string): void {
    discardedDrafts.add(id)
}
export function isDraftDiscarded(id: string): boolean {
    return discardedDrafts.has(id)
}

/**
 * Ask the conversation showing session `sessionId` to bring its live browser up (from outside any surface: a
 * browser card on the voice call screen). The AgentSurface for that session listens; nothing happens if none is
 * mounted (opening the conversation then shows the live browser anyway).
 */
export const BROWSER_OPEN_EVENT = 'lh:agent-browser-open'
export function requestBrowser(sessionId: string): void {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(BROWSER_OPEN_EVENT, { detail: { sessionId } }))
}

/** The workspace of the surface this component is rendered in, if any. */
export function useWorkspace(): AgentWorkspace | null {
    return inject(WORKSPACE_KEY, null)
}
